// src/pages/Experts.js
import { useState, useEffect, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Container, Chip, Avatar, Button, useTheme, alpha, Paper,
  TextField, InputAdornment, MenuItem, FormControl, InputLabel,
  Select, OutlinedInput, Divider, Tab, Tabs, CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  ClearAll as ClearAllIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import * as industryApi from '../services/industry';
import * as userApi from '../services/user';
import { User } from '../services/user';
import Sidebar from '../components/common/Sidebar';


const drawerWidth = 240;
const MotionCard = motion(Card);

const segments = [
  'All Segments',
  'Government',
  'Investment Institution',
  'Private Sector Company',
  'RDI',
  'University',
  'Startup',
  'Solo-entrepreneur'
];

const ExpertsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [experts, setExperts] = useState<User[]>([]);
  const [filteredExperts, setFilteredExperts] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [industries, setIndustries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch experts and industries
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [expertsData, industriesData] = await Promise.all([
          userApi.getExperts(),
          industryApi.getAllIndustries()
        ]);
        setExperts(expertsData);
        setFilteredExperts(expertsData);
        setIndustries(industriesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...experts];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(expert =>
        expert.firstName?.toLowerCase().includes(term) ||
        expert.lastName?.toLowerCase().includes(term) ||
        expert.title?.toLowerCase().includes(term) ||
        expert.organizationName?.toLowerCase().includes(term)
      );
    }

    // Filter by sectors
    if (selectedSectors.length > 0) {
      filtered = filtered.filter(expert =>
        expert.selectedSectors?.some(sector =>
          selectedSectors.includes(sector)
        )
      );
    }

    // Filter by segment
    if (selectedSegment && selectedSegment !== 'All Segments') {
      filtered = filtered.filter(expert =>
        expert.segment === selectedSegment
      );
    }

    setFilteredExperts(filtered);
  }, [experts, searchTerm, selectedSectors, selectedSegment, selectedTag]);

  const handleExpertClick = (expertId: string) => {
    navigate(`/expert/${expertId}`);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSectorChange = (event: { target: { value: any; }; }) => {
    const { value } = event.target;
    setSelectedSectors(value);
  };

  const handleTabChange = (_event: any, newValue: SetStateAction<number>) => {
    setActiveTab(newValue);
    
    // Set tag filter based on tab
    switch(newValue) {
      case 0:
        setSelectedTag('');
        break;
      case 1:
        setSelectedTag('Expert');
        break;
      case 2:
        setSelectedTag('Member');
        break;
      default:
        setSelectedTag('');
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedSectors([]);
    setSelectedSegment('');
    setActiveTab(0);
    setSelectedTag('');
  };

  // Get unique sectors from all industries
  const availableSectors = Array.from(new Set(industries.flatMap(ind => [ind.name])));

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar open={isSidebarOpen} onClose={toggleSidebar} isAuthenticated={undefined} user={undefined} />
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${isSidebarOpen ? drawerWidth : 0}px)` },
          ml: { sm: isSidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Container maxWidth="xl">
          {/* Page Header */}
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.dark, 0.85)})`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '40%',
                height: '100%',
                opacity: 0.1,
                background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
                transform: 'translateX(30%)'
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ fontWeight: 700, mb: 1 }}
              >
                Industry Experts
              </Typography>
              
              <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 700 }}>
                Connect with industry specialists, researchers, and advisors to gain insights and support for your business ventures in Saudi Arabia
              </Typography>
            </Box>
          </Paper>

          {/* Tabs and Filters */}
          <Paper
            elevation={2}
            sx={{
              mb: 4,
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 64,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }
                }}
              >
                <Tab label="All" icon={<PersonIcon />} iconPosition="start" />
                <Tab label="Experts" icon={<SchoolIcon />} iconPosition="start" />
                <Tab label="Members" icon={<BusinessIcon />} iconPosition="start" />
              </Tabs>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search experts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="sector-select-label">Sectors</InputLabel>
                    <Select
                      labelId="sector-select-label"
                      multiple
                      value={selectedSectors}
                      onChange={handleSectorChange}
                      input={<OutlinedInput label="Sectors" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {availableSectors.map((sector) => (
                        <MenuItem key={sector} value={sector}>
                          {sector}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="segment-select-label">Segment</InputLabel>
                    <Select
                      labelId="segment-select-label"
                      value={selectedSegment}
                      onChange={(e) => setSelectedSegment(e.target.value)}
                      label="Segment"
                    >
                      {segments.map((segment) => (
                        <MenuItem key={segment} value={segment}>
                          {segment}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ClearAllIcon />}
                    onClick={handleClearFilters}
                    sx={{ height: '56px' }}
                  >
                    Clear All
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Results Count */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" color="text.secondary">
              Showing {filteredExperts.length} {filteredExperts.length === 1 ? 'expert' : 'experts'}
            </Typography>
          </Box>

          {/* Experts Grid */}
          <Grid container spacing={3}>
            {filteredExperts.map((expert) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={expert.id}>
                <MotionCard
                  whileHover={{ y: -5 }}
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    height: '100%',
                    overflow: 'hidden'
                  }}
                >
                  <CardActionArea onClick={() => handleExpertClick(expert.id)}>
                    <Box
                      sx={{
                        height: 160,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      {/* Profile Photo */}
                      <Avatar
                        src={expert.photoUrl || `https://ui-avatars.com/api/?name=${expert.firstName}+${expert.lastName}&background=random&size=150`}
                        alt={`${expert.firstName} ${expert.lastName}`}
                        sx={{
                          width: 100,
                          height: 100,
                          border: `4px solid ${theme.palette.background.paper}`,
                          boxShadow: '0 4px 10px rgba(0,0,0,0.08)'
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={expert.expertProfile?.tag || 'Expert'}
                          size="small"
                          color="secondary"
                          sx={{ borderRadius: 1, mb: 1 }}
                        />
                      </Box>

                      {/* Logo and Name Section */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {/* Company Logo */}
                        <Avatar
                          src={expert.companyLogoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(expert.organizationName || 'Company')}&background=random&size=60&bold=true`}
                          alt={expert.organizationName || 'Company'}
                          variant="rounded"
                          sx={{
                            width: 60,
                            height: 60,
                            border: `2px solid ${theme.palette.divider}`,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                          }}
                        />

                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" gutterBottom fontWeight={600}>
                            {expert.firstName} {expert.lastName}
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {expert.title}
                          </Typography>

                          <Typography variant="body2" fontWeight={500}>
                            {expert.organizationName}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Typography variant="caption" color="text.secondary">
                        Sectors:
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {expert.selectedSectors?.map((sector, idx) => (
                          <Chip
                            key={idx}
                            label={sector}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1 }}
                          />
                        ))}
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.primary.main, mt: 2 }}>
                        <Typography variant="button" sx={{ mr: 0.5 }}>
                          View Profile
                        </Typography>
                        <ArrowForwardIcon fontSize="small" />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </MotionCard>
              </Grid>
            ))}
            
            {filteredExperts.length === 0 && (
              <Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                  No experts match your criteria
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ExpertsPage;